/**
 * Price-axis tags: their text colour, and where they sit when two collide.
 *
 * Pure, so the arithmetic is tested without a canvas; `scichart-builder` reads
 * the pixels off the axis and writes the answer back.
 *
 * **Text by contrast, not by convention** (owner, 2026-10-21). Level tags wore
 * white text on their line's colour and value tags the theme's background —
 * white on yellow and on light green was unreadable, and dark-on-dark the
 * mirror of it. A tag now takes near-black or white, whichever reads better
 * against its fill (WCAG relative luminance), on every theme and for any
 * colour a user picks.
 *
 * **Level tags give way; value tags do not.** A series' last-value tag is the
 * price now and must sit exactly on it; a level tag (Bar Intelligence, PA
 * levels) that would overlap one — or another level — moves to the nearest free
 * space above or below, and goes back to its own price as soon as there is room.
 */

const DARK = "#0e1116";
const LIGHT = "#ffffff";

/** `[r, g, b]` 0-255 from `#rgb`, `#rrggbb`, `#rrggbbaa` or `rgb(a)(…)`; null otherwise. */
function rgbOf(color: string): [number, number, number] | null {
  const c = color.trim().toLowerCase();
  const hex = /^#([0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/.exec(c);
  if (hex) {
    const h = hex[1]!;
    if (h.length === 3) return [0, 1, 2].map((i) => parseInt(h[i]! + h[i]!, 16)) as [number, number, number];
    return [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16)) as [number, number, number];
  }
  const fn = /^rgba?\(\s*([\d.]+)[\s,]+([\d.]+)[\s,]+([\d.]+)/.exec(c);
  if (fn) return [Number(fn[1]), Number(fn[2]), Number(fn[3])];
  return null;
}

function luminance([r, g, b]: [number, number, number]): number {
  const lin = (v: number) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
}

function contrast(a: number, b: number): number {
  return (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05);
}

/** Near-black or white, whichever reads better on `fill`. White when the fill is not a colour this can read. */
export function readableTextOn(fill: string | undefined): string {
  const rgb = fill ? rgbOf(fill) : null;
  if (!rgb) return LIGHT;
  const l = luminance(rgb);
  const dark = luminance(rgbOf(DARK)!);
  return contrast(l, dark) >= contrast(l, 1) ? DARK : LIGHT;
}

export interface AxisTag {
  key: string;
  /** Where its price puts it, in the axis's pixels (down is larger). */
  y: number;
  /** A value tag: never moves, others move around it. */
  fixed: boolean;
}

/**
 * Where each tag goes so none overlaps another, every tag `height` tall and
 * centred on its `y`.
 *
 * Fixed tags stay put. Movable ones are placed top to bottom at their own `y`
 * when free, else at the nearest free spot above or below what is in the way
 * (below on a tie, so a tag stays ordered with the levels under it). A tag
 * already free is returned at exactly its `y`.
 */
export function layoutAxisTags(tags: readonly AxisTag[], height: number): Map<string, number> {
  const out = new Map<string, number>();
  const taken: number[] = [];
  for (const t of tags) {
    if (t.fixed) {
      out.set(t.key, t.y);
      taken.push(t.y);
    }
  }
  const clash = (y: number) => taken.find((o) => Math.abs(o - y) < height);
  const movable = tags.filter((t) => !t.fixed).sort((a, b) => a.y - b.y);
  for (const t of movable) {
    let y = t.y;
    for (let guard = 0; guard < 4 * tags.length + 4; guard += 1) {
      const hit = clash(y);
      if (hit === undefined) break;
      // The nearest free side of everything stacked at `hit`, searched outward.
      const below = freeFrom(hit + height, +1);
      const above = freeFrom(hit - height, -1);
      y = Math.abs(below - t.y) <= Math.abs(above - t.y) ? below : above;
    }
    out.set(t.key, y);
    taken.push(y);
  }
  return out;

  function freeFrom(start: number, dir: 1 | -1): number {
    let y = start;
    for (let guard = 0; guard < 4 * tags.length + 4; guard += 1) {
      const hit = taken.find((o) => Math.abs(o - y) < height);
      if (hit === undefined) return y;
      y = hit + dir * height;
    }
    return y;
  }
}
